import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
import { DateEx } from '../../../../functions/date-ex';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { ClaimWCClaimCard } from '../../../../models/api/view/claim/summary/claim-wc-claim-card';
import { FormatClaimPipe } from '../../../../pipes/format-claim.pipe';
import { BaseComponent } from '../../../base/base.component';
import { MoreButtonComponent } from '../../../core/buttons/more-button.component';
import { WatchButtonComponent } from '../../../core/buttons/watch-button.component';
import { TagComponent, TagType } from '../../../core/labels/tag.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../../core/panels/loading-panel.component';
import { ClaimFlagsComponent } from './claim-flags.component';

@Component({
  selector: 'app-wc-claim-card',
  imports: [
    CommonModule,
    DetailPanelComponent,
    DetailItemComponent,
    TagComponent,
    ActionRowComponent,
    MoreButtonComponent,
    WatchButtonComponent,
    LoadingPanelComponent,
    FormatClaimPipe,
    ClaimFlagsComponent,
  ],
  template: `
    <app-loading-panel
      [state]="state"
      [error]="error"
      [loadingHeight]="246"
      loadingStyle="Box"
    >
      <ng-template #loaded>
        @if (claimCard) {
          <div class="tw-w-full">
            <div
              class="tw-flex tw-flex-col md:tw-flex-row tw-gap-0 md:tw-gap-8 tw-w-full"
            >
              <!-- Column 1 -->
              <app-detail-panel [dataSource]="claimCard" class="tw-w-full">
                <app-detail-item label="Claim Number">
                  <ng-template let-data #value>
                    <div class="tw-flex tw-items-center">
                      <b>{{
                        data.claimNumber | formatClaim: data.fileNumFormat
                      }}</b>
                      <app-watch-button
                        [recordSource]="claim.recordSource"
                        [recordId]="claim.recordId"
                      />
                    </div>
                  </ng-template>
                </app-detail-item>
                <app-detail-item label="Status">
                  <ng-template let-data #value>
                    <app-tag [type]="statusType">{{ statusDisplay }}</app-tag>
                  </ng-template>
                </app-detail-item>
                <app-detail-item label="Date of Loss">
                  <ng-template let-data #value>
                    {{
                      DateEx.formatDateWithTimeCode(
                        data.dateOfLoss,
                        data.eventTime
                      )
                    }}
                  </ng-template>
                </app-detail-item>
                <app-detail-item label="Jurisdiction" name="jurisdiction" />
              </app-detail-panel>

              <!-- Column 2 -->
              <app-detail-panel [dataSource]="claimCard" class="tw-w-full">
                @if (showCause) {
                  <app-detail-item label="Cause">
                    <ng-template let-data #value>
                      {{
                        formatCodeDescription(data.cause, data.causeDescription)
                      }}
                    </ng-template>
                  </app-detail-item>
                }
                @if (showNatureResult) {
                  <app-detail-item label="Nature/result" labelCase="none">
                    <ng-template let-data #value>
                      {{
                        formatCodeDescription(
                          data.natureResult,
                          data.natureResultDescription
                        )
                      }}
                    </ng-template>
                  </app-detail-item>
                }
                @if (showPartTarget) {
                  <app-detail-item label="Part/target" labelCase="none">
                    <ng-template let-data #value>
                      {{
                        formatCodeDescription(
                          data.partTarget,
                          data.partTargetDescription
                        )
                      }}
                    </ng-template>
                  </app-detail-item>
                }
                @if (
                  (claimRow.coverageCode && claimRow.coverageCode === 'OT') ||
                  !isPrClaim
                ) {
                  <app-detail-item
                    label="Body Side"
                    name="bodySide"
                    labelCase="none"
                  />
                }
              </app-detail-panel>
            </div>

            <!-- Event Description -->
            @if (showEventDescription && claimCard.eventDescription) {
              <div>
                <div class="tw-font-bold tw-py-2">Event description</div>
                <div class="tw-pb-4 tw-whitespace-pre-line">
                  {{ claimCard.eventDescription }}
                </div>
              </div>
            }

            <!-- Claim Flags and More Button -->
            <div
              class=" tw-pt-2 tw-pb-4 tw-flex tw-justify-left tw-items-center tw-gap-4"
            >
              <app-claim-flags left1 [flags]="claimCard" />
            </div>
            <app-action-row>
              @if (isPrClaim && propertyToken) {
                <app-more-button
                  right1
                  (click)="this.view.navigateCurrentClaim(['property'])"
                  [ariaLabel]="
                    'Navigate to More Property for ' + claimRow?.fileNumber
                  "
                />
              } @else if (isWcClaim) {
                <app-more-button
                  right1
                  [ariaLabel]="
                    'Navigate to More Claim Details for ' +
                    claimCard?.claimNumber
                  "
                  (click)="
                    this.view.navigateCurrentClaim(['workers-compensation'])
                  "
                />
              } @else {
                <app-more-button
                  right1
                  [ariaLabel]="
                    'Navigate to More Claim Details for ' +
                    claimCard?.claimNumber
                  "
                />
              }
            </app-action-row>
          </div>
        }
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class WcClaimCardComponent extends BaseComponent implements OnInit {
  protected DateEx = DateEx;
  claimRow: ClaimRow;
  claimCard: ClaimWCClaimCard | null = null;
  state: LoadingState = 'Loading';
  error: any = null;
  claim: any = null;
  propertyToken = false;
  isPrClaim = false;
  isWcClaim = false;
  statusType: TagType = 'Success';
  statusDisplay = '';

  showCause = true;
  showNatureResult = true;
  showPartTarget = true;
  showEventDescription = true;

  // Mock data for non-implemented LOBs
  mockClaimData = {
    claimNumber: 'C283001140-0001-01',
    status: 'Open - Approved',
    lossDate: DateEx.addDays(-7, new Date()),
    workRelated: true,
    cause: '2018 Struck By Falling Object',
    nature: '2800 Fracture',
    part: '1900 Facial Bones',
    injuryType: 'Lost Time',
    bodySide: 'Left',
  };

  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  async ngOnInit(): Promise<void> {
    this.initFieldSecurity();
    const currentClaim = this.view.currentClaim();
    this.claimRow = currentClaim;
    this.propertyToken = this.view.authorize('Property');
    this.isPrClaim = currentClaim.lineOfBusiness === 'PR';
    this.isWcClaim = currentClaim.lineOfBusiness === 'WC';
    // List of implemented LOBs
    const implementedLOBs = ['WC'];

    if (currentClaim && implementedLOBs.includes(currentClaim.lineOfBusiness)) {
      // Use real API data for implemented LOBs
      this.claim = currentClaim;
      this.claimCard = await this.api.claimSummary.getWCClaimCard(
        this.claim.recordSource,
        this.claim.recordId,
      );
    } else {
      // Use mock data for non-implemented LOBs
      this.claim = this.mockClaimData;
    }

    if (this.claimCard) {
      this.statusDisplay =
        (this.claimCard.status || '') +
        (this.claimCard.subStatus ? ' - ' + this.claimCard.subStatus : '');
      this.statusType = this.getStatusType(this.claimCard.status);
    }
  }

  private initFieldSecurity(): void {
    const restricted = this.session.user?.fieldSecurity?.restrictedFields ?? [];
    this.showCause = !restricted.includes('Cause');
    this.showNatureResult = !restricted.includes('NatureResult');
    this.showPartTarget = !restricted.includes('PartTarget');
    this.showEventDescription = !restricted.includes('EventDescription');
  }

  private getStatusType(status: string): TagType {
    const statusUpper = status?.toUpperCase() || '';
    if (statusUpper.includes('OPEN') || statusUpper.includes('REOPEN')) {
      return 'Success';
    }
    if (statusUpper.includes('CLOSED')) {
      return 'Error';
    }
    if (statusUpper.includes('INCIDENT')) {
      return 'Warn';
    }
    return 'Success';
  }

  /**
   * Format code and description as "code description"
   */
  formatCodeDescription(
    code: string | null,
    description: string | null,
  ): string {
    if (code && description) {
      return `${code} ${description}`;
    } else if (code) {
      return code;
    } else if (description) {
      return description;
    }
    return '';
  }
}
